import MedicineDictionaryModel from '@/entities/medicine-dictionary/model/medicineDictionary.model';
import { MedicineDictionaryItemByPublicIdQuery } from '@/types/bff/gql-schema';
import { QueryClient } from '@tanstack/react-query';
import { useMedicineDictionaryItemByPublicIdQuery } from './medicine-dictionary.gql.impl';

const parseMedicineDictionary = (data: MedicineDictionaryItemByPublicIdQuery) => {
  return new MedicineDictionaryModel({
    id: data.medicineDictionaryItemByPublicId.id,
    publicId: data.medicineDictionaryItemByPublicId.publicId,
    basicInfo: {
      name: data.medicineDictionaryItemByPublicId.basicInfo.name,
      simplifiedName: data.medicineDictionaryItemByPublicId.basicInfo.simplifiedName,
      pharmaceuticalEffectLiteracy: data.medicineDictionaryItemByPublicId.basicInfo.pharmaceuticalEffectLiteracy,
      medicineImage: data.medicineDictionaryItemByPublicId.basicInfo.medicineImage ?? undefined,
      medicinePictogramImages: data.medicineDictionaryItemByPublicId.basicInfo.medicinePictogramImages,
    },
    classificationInfo: {
      ethicalType: data.medicineDictionaryItemByPublicId.classificationInfo.ethicalType,
    },
    companyInfo: {
      companyName: data.medicineDictionaryItemByPublicId.companyInfo.companyName,
    },
    ingredientInfo: {
      formattedIngredientName:
        data.medicineDictionaryItemByPublicId.ingredientInfo.formattedIngredientName ?? undefined,
    },
    medicineGuide: {
      indications: data.medicineDictionaryItemByPublicId.medicineGuide.indications,
      dosages: data.medicineDictionaryItemByPublicId.medicineGuide.dosages,
      dosageHtml: data.medicineDictionaryItemByPublicId.medicineGuide.dosageHtml,
      sideEffects: data.medicineDictionaryItemByPublicId.medicineGuide.sideEffects,
      cautions: data.medicineDictionaryItemByPublicId.medicineGuide.cautions,
      storages: data.medicineDictionaryItemByPublicId.medicineGuide.storages,
      videoLink: data.medicineDictionaryItemByPublicId.medicineGuide.videoLink,
      interactionHtml: data.medicineDictionaryItemByPublicId.medicineGuide.interactionHtml,
    },
    packagingInfo: {
      image: data.medicineDictionaryItemByPublicId.packagingInfo.image ?? undefined,
    },
  });
};

const useMedicineDictionaryItemByPublicId = (publicId: string) => {
  const { data: medicineDictionaryItemByPublicId } = useMedicineDictionaryItemByPublicIdQuery(
    {
      publicId,
    },
    {
      enabled: !!publicId,
      select: parseMedicineDictionary,
      staleTime: 1000 * 60 * 5, // 5분 - SSR과 동일한 설정
    }
  );

  return {
    medicineDictionaryItemByPublicId,
  };
};

export default useMedicineDictionaryItemByPublicId;

useMedicineDictionaryItemByPublicId.prefetchQuery = async (queryClient: QueryClient, publicId: string) => {
  return queryClient.prefetchQuery({
    queryKey: useMedicineDictionaryItemByPublicIdQuery.getKey({ publicId }),
    queryFn: useMedicineDictionaryItemByPublicIdQuery.fetcher({ publicId }),
    staleTime: 1000 * 60 * 5, // 5분
  });
};

useMedicineDictionaryItemByPublicId.getKey = (publicId: string) => {
  return useMedicineDictionaryItemByPublicIdQuery.getKey({ publicId });
};
